import {debug} from '../../utils/debug'
import {createQueryIndex, IndexedValues, QueryIndex, QueryNamespace} from '../queryIndex/queryIndex'
import type {
    DocumentDataTypes,
    FilterGetter,
    IdGenerator,
    IndexKey,
    KeyValPredicate,
    Namespace,
    Null,
    NullUndefined,
    PostSetOperation,
    PostTransactionOperation,
    PostTransactionSideEffect,
    Transaction
} from '../../types'
import {createStore, createStoreFromJS, DalJsStore, DalValue, DmStore} from '../store'
import {createUniqueIdGenerator} from '../../utils/uniqueIdGenerator'
import {createSnapshotChain, SnapshotDal} from '../snapshot/snapshotDal'
import type {CoreConfig} from '../../documentManagerCore'
import type {EventEmitter} from 'events'
import {createValidator} from '../validation/dalValidation'
import type {DropCommitContext, Pointer, SimpleDalAPI, Tags} from '@wix/document-services-types'
import _ from 'lodash'
import {
    basedOnSignatureNS,
    CustomGetter,
    DalValueChangeCallback,
    Getter,
    OpenTransaction,
    PAGE_FRAGMENT_TYPE,
    SNAPSHOT_REMOVED_EVENT,
    TransactionEvent,
    TransactionEvents
} from '../documentManagerDal'
import type {SnapshotList, Store} from '../snapshot/snapshotList'
import type {TagManager} from '../snapshot/tagManager'
import {isConflicting as isConflictingValue} from '../signatures/isConflicting'
import {
    getFragmentPointer,
    getInnerPath,
    getInnerPointer,
    getInnerValue,
    getPointer,
    hasInnerPath,
    stripInnerPath
} from '../../utils/pointerUtils'
import {ReportableError, ReportableWarning} from '@wix/document-manager-utils'
import {
    createImmutableProxy,
    createImmutableProxyForMap,
    createImmutableProxyForMapOfMaps,
    deepClone
} from '@wix/wix-immutable-proxy'
import {deepCompare} from '../../utils/deepCompare'
import {deepCompareIgnoreSignature} from '../../utils/deepCompareIgnoreSignature'
import {map_findBy, map_pickBy} from '../../utils/pickBy'

const EMPTY_MAP = new Map()
const DAL_READ_VALUES = 'DAL_READ_VALUES'
const DAL_READ_INDEX = 'DAL_READ_INDEX'

const isEditorPointer = (pointer: Pointer & {documentPointer?: any}): boolean => pointer.documentPointer

const validatePointer = (pointer: Pointer & {documentPointer?: any}): void => {
    if (isEditorPointer(pointer)) {
        throw new ReportableError({
            errorType: 'editorPointer',
            message: 'Editor pointer detected, please unwrap the pointer before using it',
            extras: {pointer}
        })
    }
}

export const getPointersByNamespaceKeys = (keys: string[], namespace: string, fragmentType?: string): Pointer[] =>
    keys.map(id => getPointer(id, namespace, fragmentType === PAGE_FRAGMENT_TYPE ? {} : {fragmentType}))

const pointersInNamespace = (
    namespace: QueryNamespace | undefined,
    pointerType: string,
    fragmentType?: string
): Pointer[] => (namespace ? getPointersByNamespaceKeys([...namespace.keys()], pointerType, fragmentType) : [])

export interface CreateFragmentArgs {
    fragmentType: string
    coreConfig: CoreConfig
    queryFilterGetters: Record<string, FilterGetter>
    postSetOperations: PostSetOperation[]
    dmTypes: DocumentDataTypes
    customGetters: Record<string, CustomGetter>
    eventEmitter: EventEmitter
    lastApproved: Null<SnapshotDal>
    snapshots: SnapshotList
    tagManager: TagManager
    customGettersEnabled: boolean
}

export interface FragmentDAL extends SimpleDalAPI {
    approve(tx: SnapshotDal): void
    hasNothingToCommit(): boolean
    commitTransactionForFragment(committer?: string, skipValidations?: boolean): DmStore | null
    find(namespace: string, indexKey: IndexKey, predicate: KeyValPredicate): any
    getIndexed(indexKey: IndexKey): IndexedValues
    getIndexKeys(indexName: string): string[]
    get(pointer: Pointer): DalValue
    has(pointer: Pointer): boolean
    isDirty(pointer: Pointer): boolean
    query(namespace: string, indexKey: IndexKey, optionalPredicate?: KeyValPredicate): Record<string, DalValue>
    queryKeys(namespace: string, indexKey: IndexKey, optionalPredicate?: KeyValPredicate): any
    getIndexPointers(indexKey: IndexKey, namespace?: string): Pointer[]
    remove(pointer: Pointer): void
    set(pointer: Pointer, value: DalValue): void
    setIfChanged(pointer: Pointer, value: DalValue): void
    touch(pointer: Pointer): void
    validate(tags?: Record<string, any>): void
    validatePendingCommit(tags?: Record<string, any>): void
    createValidationBaseline(): void
    recreateValidationBaseline(): void
    sign(
        rootPointer: Pointer,
        value: DalValue,
        signOver?: Null<SnapshotDal>
    ): {value: DalValue; basedOnSignature: NullUndefined<string>}
    getBasedOnSignatureId(namespace?: string, id?: string): string
    getBasedOnSignaturePointer(pointer: Pointer): Pointer
    setBasedOnSignature(pointer: Pointer, signature: NullUndefined<string>): void
    getCurrentOpenTransaction(): Transaction
    getAllItemsInNamespace(namespace: Namespace): Pointer[]
    mergeToApprovedStore(changes: DmStore, snapshotId: string): void
    validateAndMergeToApprovedStore(changes: DmStore, snapshotId: string): void
    dropUncommittedTransaction(reason?: string, e?: any, context?: DropCommitContext): void
    hasSignature(pointer: Pointer): boolean
    removeConflictsAndReport(event: TransactionEvent, originalChange?: DmStore): void
    updateLastApprovedSnapshot(snapshot: SnapshotDal): void
    getNamespaces(): Namespace[]
}

export class FragmentDal implements FragmentDAL {
    private debugLogger = debug(`fragment-dal-${this.fragmentType}`)
    rebaseValidator: any
    postTransactionOperations: Record<string, PostTransactionOperation> = {}
    transactionDerivedMetadataOperations: Record<string, PostTransactionOperation> = {}
    updateCallbacks: DalValueChangeCallback[] = []
    validator: any

    private queryIndex: QueryIndex
    private store: DmStore
    private tentativeStore: DmStore
    private currentOpenTransaction: OpenTransaction
    private createOpenTxId: IdGenerator
    private createSignature: IdGenerator
    private shouldNotifyAboutReads = false

    constructor(
        private fragmentType: string,
        private coreConfig: CoreConfig,
        private queryFilterGetters: Record<string, FilterGetter>,
        private postSetOperations: PostSetOperation[],
        private dmTypes: DocumentDataTypes,
        private customGetters: Record<string, CustomGetter>,
        private eventEmitter: EventEmitter,
        private lastApproved: Null<SnapshotDal>,
        private snapshots: SnapshotList,
        private tagManager: TagManager,
        private customGettersEnabled: boolean
    ) {
        this.queryIndex = createQueryIndex()
        this.store = createStore()
        this.createOpenTxId = createUniqueIdGenerator(3)
        this.tentativeStore = createStore()
        this.currentOpenTransaction = this.createNewOpenTransaction()

        this.validator = createValidator(this.coreConfig, [basedOnSignatureNS], this.fragmentType)
        this.rebaseValidator = createValidator(this.coreConfig, [basedOnSignatureNS], this.fragmentType)

        this.createSignature = createUniqueIdGenerator(3, this.coreConfig.signatureSeed)
    }

    get _queryIndex() {
        return this.queryIndex
    }

    get _currentTransaction() {
        return this.currentOpenTransaction
    }

    get _store() {
        return this.store
    }

    get _tentativeStore() {
        return this.tentativeStore
    }

    get _snapshots() {
        return this.snapshots
    }

    updateLastApprovedSnapshot(snapshot: SnapshotDal) {
        this.lastApproved = snapshot
    }

    /**
     * Merge all the changes since the last approved snapshot up to the new approved
     * snapshot into the main store and update the approved snapshot pointer.
     * Rebuild the tentative store with the remaining unapproved changes.
     * @param tx is the approved snapshot
     */
    approve(tx: SnapshotDal): void {
        const diff: DmStore = createStoreFromJS(tx.mutableDiff(this.lastApproved, this.fragmentType))
        this.store.merge(diff)

        this.tentativeStore = createStoreFromJS(this.snapshots.last()?.mutableDiff(tx, this.fragmentType) as DalJsStore)
    }

    enableShouldNotifyAboutReads() {
        this.shouldNotifyAboutReads = true
    }

    private validateTransactionStoreSize(store: DmStore, tags?: Tags) {
        const storeActionsAmount = store.size()
        if (storeActionsAmount > this.coreConfig.transactionActionsLimit) {
            const err = new ReportableWarning({
                errorType: 'exceedsMaxActionsPerTransaction',
                message: `Exceeds actions reached ${storeActionsAmount}`,
                extras: {tags}
            })
            this.coreConfig.logger.captureError(err)
            if (this.coreConfig.experimentInstance.isOpen('dm_failOnExceedsMaxActionsPerTransaction')) {
                throw err
            }
        }
    }

    find(namespace: string, indexKey: IndexKey, predicate: KeyValPredicate) {
        this.verifyQueryParams(namespace)

        const namespaces = this.queryIndex.getIndexedValues(indexKey)
        const queryResult = namespaces.get(namespace)
        const findResult = map_findBy(queryResult, predicate)

        return createImmutableProxy(findResult)
    }

    getIndexed(indexKey: IndexKey): IndexedValues {
        const namespaces = this.queryIndex.getIndexedValues(indexKey)
        if (this.shouldNotifyAboutReads) {
            this.eventEmitter.emit(DAL_READ_INDEX, {namespacesKeys: Array.from(namespaces.keys()), indexKey})
        }
        return createImmutableProxyForMapOfMaps(namespaces ?? EMPTY_MAP) as IndexedValues
    }

    getIndexKeys(indexName: string): string[] {
        return this.queryIndex.getIndexKeys(indexName)
    }

    get(pointer: Pointer) {
        return createImmutableProxy(this.getRawValue(pointer))
    }

    getWithPath(pointer: Pointer, innerPath: string | string[]) {
        return this.get(getInnerPointer(pointer, innerPath))
    }

    has(pointer: Pointer): boolean {
        return !_.isNil(this.getRawValue(pointer))
    }

    isDirty(pointer: Pointer): boolean {
        return this.currentOpenTransaction.store.has(pointer)
    }

    query(namespace: string, indexKey: IndexKey, optionalPredicate?: KeyValPredicate | undefined): Record<string, any> {
        const result = this.queryRaw(namespace, indexKey, optionalPredicate)
        return createImmutableProxyForMap(result ?? EMPTY_MAP)
    }

    queryKeys(namespace: string, indexKey: IndexKey, optionalPredicate?: KeyValPredicate | undefined) {
        const result = this.queryRaw(namespace, indexKey, optionalPredicate)
        return result ? [...result.keys()] : []
    }

    getIndexPointers(indexKey: IndexKey, namespace?: string | undefined, fragmentType?: string): Pointer[] {
        const namespaces = this.queryIndex.getIndexedValues(indexKey)
        let pointers: Pointer[]
        if (namespace) {
            const namespaceValues = namespaces.get(namespace)
            pointers = pointersInNamespace(namespaceValues, namespace, fragmentType)
        } else {
            pointers = [...namespaces.entries()].flatMap(([ns, namespaceValues]) =>
                pointersInNamespace(namespaceValues, ns)
            )
        }
        if (this.shouldNotifyAboutReads) {
            this.eventEmitter.emit(DAL_READ_VALUES, pointers)
        }
        return pointers
    }

    remove(pointer: Pointer): void {
        const innerPath = getInnerPath(pointer)
        const rootPointer = stripInnerPath(pointer)
        let newValue
        if (innerPath.length > 0) {
            newValue = deepClone(this.getRawValue(rootPointer))
            _.unset(newValue, innerPath)
        }
        this.signAndUpdateTransactionAndIndex(rootPointer, newValue)
    }

    _set(pointer: Pointer, value: DalValue): void {
        validatePointer(pointer)
        const innerPath = getInnerPath(pointer)
        const rootPointer = stripInnerPath(pointer)
        let newValue = deepClone(value)
        if (innerPath.length > 0) {
            const rootValue = deepClone(this.getRawValue(rootPointer)) || {}
            newValue = _.setWith(rootValue, innerPath, newValue, Object)
        }
        this.signAndUpdateTransactionAndIndex(rootPointer, newValue)
    }

    set(pointer: Pointer, value: any): void {
        this.setIfChanged(pointer, value)
    }

    setIfChanged(pointer: Pointer, value: any): void {
        if (this.isChanged(pointer, value)) {
            this._set(pointer, value)
        }
    }

    modify<A, B = A>(pointer: Pointer, f: (a: A) => B): void {
        return this.set(pointer, f(this.get(pointer)))
    }

    touch(pointer: Pointer): void {
        return this._set(pointer, this.getterForSetAndTouch(pointer))
    }

    getLastSnapshot(): SnapshotDal | null {
        return this.snapshots.last()
    }

    validate(tags?: Record<string, any>) {
        const tentativeAndAccepted = this.getMergedStore(false)
        this.validator.validateStore(tentativeAndAccepted, false, tags)
    }

    validatePendingCommit(tags?: Record<string, any> | undefined): void {
        this.validateTransactionStoreSize(this.currentOpenTransaction.store, tags)
        return this.validator.validateStore(this.currentOpenTransaction.store, true, tags)
    }

    createValidationBaseline() {
        const tentativeAndAccepted = this.getMergedStore(false)
        this.validator.createBaseline(tentativeAndAccepted)
    }

    recreateValidationBaseline() {
        const tentativeAndAccepted = this.getMergedStore(false)
        this.validator.recreateBaseline(tentativeAndAccepted)
    }

    sign(
        rootPointer: Pointer,
        value: any,
        signOver?: Null<SnapshotDal> | undefined
    ): {value: any; basedOnSignature: NullUndefined<string>} {
        let basedOnSignature
        if (value && this.hasSignature(rootPointer)) {
            const previousValue = signOver ? signOver.getValue(rootPointer) : this.getCommittedValue(rootPointer)
            basedOnSignature = previousValue ? previousValue.metaData?.sig ?? null : undefined
            value.metaData = Object.assign(value.metaData || {}, {
                sig: this.createSignature()
            })
        }
        return {value, basedOnSignature}
    }

    getBasedOnSignatureId(namespace?: string | undefined, id?: string | undefined): string {
        return `${namespace}$${id}`
    }

    getBasedOnSignaturePointer(pointer: Pointer): Pointer {
        const {id, type, fragmentType} = pointer
        return getPointer(this.getBasedOnSignatureId(type, id), basedOnSignatureNS, {fragmentType})
    }

    getBasedOnSignature(pointer: Pointer): NullUndefined<string> {
        return this.get(this.getBasedOnSignaturePointer(pointer))
    }

    setBasedOnSignature(pointer: Pointer, basedOnSignature: NullUndefined<string>): void {
        this._set(this.getBasedOnSignaturePointer(pointer), basedOnSignature)
    }

    getCurrentOpenTransaction() {
        return createImmutableProxy({
            id: 'openTransaction',
            items: this.currentOpenTransaction.store.getValues()
        })
    }

    getLastApprovedSnapshot(): SnapshotDal {
        if (!this.lastApproved) {
            throw new Error('Empty DAL does not have a last approved snapshot')
        }
        return this.lastApproved
    }

    dropUncommittedTransaction(reason?: string, e?: any, context: DropCommitContext = {}) {
        if (this.currentOpenTransaction.store.isEmpty()) {
            return
        }
        const pointersToUpdate = this.currentOpenTransaction.store.keys()
        const pointerToReport = _(pointersToUpdate)
            .reject(ptr => {
                return ptr.type === 'basedOnSignature'
            })
            .map('id')
            .join(',')
        console.error('dropUncommittedTransaction', e, context)
        this.coreConfig.logger.captureError(
            new ReportableError({
                message: 'dropUncommittedTransaction',
                errorType: 'dropUncommittedTransaction',
                tags: {
                    attempt: this.currentOpenTransaction.attempt,
                    id: this.currentOpenTransaction.id
                },
                extras: {
                    reason,
                    appDefinitionId: _.get(context, ['appDefinitionId'], ''),
                    stack: e?.stack ? e.stack.slice(0, 3000) : '',
                    pointersToUpdate: pointerToReport
                }
            })
        )
        this.createNewOpenTransaction()
        this.updateIndex(pointersToUpdate)

        const revertedStore = createStore()
        pointersToUpdate.forEach(pointer => {
            revertedStore.set(pointer, this.getMergedStoreValue(pointer))
        })
        this.report(revertedStore, TransactionEvents.TRANSACTION_REJECTED)
    }

    hasNothingToCommit(): boolean {
        return this.currentOpenTransaction.store.isEmpty()
    }

    withoutCustomGetters(func: Function) {
        return (...args: any[]) => {
            try {
                this.customGettersEnabled = false
                return func(...args)
            } finally {
                this.customGettersEnabled = true
            }
        }
    }

    hasSignature(pointer: Pointer): boolean {
        const idsWithSignature = this.dmTypes[pointer.type]?.idsWithSignature
        if (idsWithSignature) {
            return idsWithSignature.has(pointer.id)
        }
        return this.dmTypes[pointer.type]?.hasSignature ?? false
    }

    private isRegisteredNamespace(type: string): boolean {
        return !!this.dmTypes[type]
    }

    getRegisteredTypes(): DocumentDataTypes {
        return this.dmTypes
    }

    private verifyQueryParams(namespace: string): void {
        if (!this.isRegisteredNamespace(namespace)) {
            console.log(`Namespace ${namespace} is not registered`)
            throw new Error(`Namespace ${namespace} is not registered`)
        }
    }

    private queryRaw(
        namespace: string,
        indexKey: IndexKey,
        optionalPredicate?: KeyValPredicate
    ): QueryNamespace | undefined {
        const namespaces = this.queryIndex.getIndexedValues(indexKey)
        let queryNamespace = namespaces.get(namespace)
        if (optionalPredicate && queryNamespace) {
            queryNamespace = map_pickBy(queryNamespace, optionalPredicate)
        }
        if (this.shouldNotifyAboutReads) {
            this.eventEmitter.emit(DAL_READ_INDEX, {namespacesKeys: [namespace], indexKey})
        }
        return queryNamespace
    }

    private getFromStores(stores: DmStore[], rootPointer: Pointer): DalValue {
        const foundStore = stores.find((s: DmStore) => s.has(rootPointer))
        return foundStore ? foundStore.get(rootPointer) : undefined
    }

    private getMergedStoreValue(pointer: Pointer): any {
        return this.getFromStores([this.currentOpenTransaction.store, this.tentativeStore, this.store], pointer)
    }

    private getCommittedValue(pointer: Pointer): DalValue {
        return this.getFromStores([this.tentativeStore, this.store], pointer)
    }

    private getApprovedValue(pointer: Pointer): DalValue {
        return this.getFromStores([this.store], pointer)
    }

    private getRootValueFromCustomGetterOrStore(rootPointer: Pointer): DalValue {
        const customGetterArgument = {
            queryFilterGetters: this.queryFilterGetters,
            query: this.query.bind(this),
            get: (ptr: Pointer) => createImmutableProxy(this.getMergedStoreValue(ptr))
        }
        if (this.customGettersEnabled) {
            const customGetter = this.customGetters[rootPointer.type]
            if (customGetter) {
                return customGetter(customGetterArgument, rootPointer)
            }
        }
        return this.getCommittedValue(rootPointer)
    }

    private getRawValue(pointer: Pointer): DalValue {
        validatePointer(pointer)
        if (this.shouldNotifyAboutReads) {
            this.eventEmitter.emit(DAL_READ_VALUES, [pointer])
        }
        const transactionStore = this.currentOpenTransaction.store
        const rootPointer = stripInnerPath(pointer)
        let rootValue
        if (transactionStore.has(rootPointer)) {
            rootValue = transactionStore.get(rootPointer)
        } else {
            rootValue = this.getRootValueFromCustomGetterOrStore(rootPointer)
        }
        return getInnerValue(pointer, rootValue)
    }

    private createNewOpenTransaction() {
        const newTransaction = {
            attempt: 0,
            id: this.createOpenTxId(),
            store: createStore()
        }
        this.currentOpenTransaction = newTransaction
        return newTransaction
    }

    private callPostTransactionOperationsAndAccumulateAsyncSideEffects(
        transaction: Transaction
    ): null | PostTransactionSideEffect {
        const asyncSideEffects = _(this.postTransactionOperations)
            .map(cb => cb(transaction))
            .compact()
            .value() as PostTransactionSideEffect[]

        const hasSideEffects = !_.isEmpty(asyncSideEffects)
        if (hasSideEffects) {
            return async () => {
                await Promise.all(asyncSideEffects.map(p => p()))
            }
        }
        return null
    }

    private callTransactionDerivedMetadata(transaction: Transaction): void {
        _.forEach(this.transactionDerivedMetadataOperations, cb => cb(transaction))
    }

    report(reportStore: Store, event: TransactionEvent): void {
        if (reportStore.isEmpty()) {
            return
        }
        const transaction = {
            id: 'post-transaction-report',
            items: reportStore.getValues()
        }
        const postTransactionSideEffectsFunction =
            this.callPostTransactionOperationsAndAccumulateAsyncSideEffects(transaction)
        this.eventEmitter.emit(event, postTransactionSideEffectsFunction)
    }

    /**
     * Returns true if any of the values in the store have a signature
     * that conflicts with that of the getter value for the same pointer
     * @param transactionStore
     * @param getter
     */
    private isConflicting(transactionStore: DmStore, getter: Getter): boolean {
        return transactionStore.some((pointer, value) => {
            const {id, type} = pointer
            const fragmentPointer = getPointer(id, type, {fragmentType: this.fragmentType})
            return (
                this.hasSignature(fragmentPointer) &&
                isConflictingValue(
                    value,
                    getter(fragmentPointer),
                    transactionStore.get(this.getBasedOnSignaturePointer(fragmentPointer))
                )
            )
        })
    }

    private isConflictingWithMergedValue(transactionStore: DmStore): boolean {
        return this.isConflicting(transactionStore, pointer => this.getMergedStoreValue(pointer))
    }

    private isConflictingWithCommittedValue(transactionStore: DmStore): boolean {
        return this.isConflicting(transactionStore, pointer => this.getCommittedValue(pointer))
    }

    isConflictingWithApprovedValue(transactionStore: Store): boolean {
        return this.isConflicting(transactionStore as DmStore, pointer => this.getApprovedValue(pointer))
    }

    private updateSingleIndexValue(pointer: Pointer): void {
        const newValue = this.getMergedStoreValue(pointer)
        const previousValue = this.queryIndex.updateIndex(pointer, newValue)
        this.updateCallbacks.forEach((callback: DalValueChangeCallback) => {
            callback(pointer, previousValue, newValue)
        })
    }

    private updateIndex(pointers: Pointer[]): void {
        pointers.forEach(pointer => this.updateSingleIndexValue(pointer))
    }

    private runPostSetOperations(pointer: Pointer, value: DalValue) {
        try {
            _.forEach(this.postSetOperations, postSet => postSet(pointer, value))
        } catch (error) {
            this.coreConfig.logger.captureError(error as Error, {
                tags: {postSetOp: true},
                extras: {
                    pointer,
                    value
                }
            })
            throw error
        }
    }

    private setWithSignature(pointer: Pointer, valueToSet: DalValue) {
        const {value, basedOnSignature} = this.sign(pointer, valueToSet)
        if (value && this.hasSignature(pointer)) {
            this.setBasedOnSignature(pointer, basedOnSignature)
        }
        this.currentOpenTransaction.store.set(pointer, value)
    }

    private signAndUpdateTransactionAndIndex(rootPointer: Pointer, value: DalValue) {
        this.setWithSignature(rootPointer, value)
        this.updateSingleIndexValue(rootPointer)
        this.runPostSetOperations(rootPointer, value)
    }

    private getterForSetAndTouch(pointer: Pointer) {
        if (this.coreConfig.experimentInstance.isOpen('dm_useCustomGettersForSetAndTouch')) {
            return this.getRawValue(pointer)
        }
        return this.getMergedStoreValue(pointer)
    }

    private isChanged(pointer: Pointer, value: DalValue): boolean {
        const currentValue = this.getterForSetAndTouch(pointer)
        if (hasInnerPath(pointer)) {
            const currentInnerValue = getInnerValue(pointer, currentValue)
            return !deepCompare(currentInnerValue, value)
        }

        return !deepCompareIgnoreSignature(currentValue, value)
    }

    private isSameValue(a: DalValue, b: DalValue) {
        const sigA = a?.metaData?.sig
        const sigB = b?.metaData?.sig
        return sigA || sigB ? sigA === sigB : deepCompare(a, b)
    }

    private collectChanges(oldTentativeStore: DmStore, originalChange?: DmStore): DmStore {
        const changes = createStore()

        oldTentativeStore.forEach((pointer, otherValue) => {
            const dalValue = this.getCommittedValue(pointer)
            if (!this.isSameValue(dalValue, otherValue)) {
                changes.set(pointer, dalValue)
            }
        })

        if (originalChange) {
            originalChange.forEach((pointer, value) => {
                if (!oldTentativeStore.has(pointer)) {
                    changes.set(pointer, value)
                }
            })
        }

        return changes
    }

    getMergedStore(includeCurrentTransaction: boolean): Store {
        const mergedStore = createStore()
        mergedStore.merge(this.store)
        mergedStore.merge(this.tentativeStore)
        if (includeCurrentTransaction) {
            mergedStore.merge(this.currentOpenTransaction.store)
        }

        return mergedStore
    }

    getMergedNamespace(namespace: Namespace): Record<string, DalValue> {
        const mergedNamespace = {}
        this.store.applyTypeValuesWithDelete(namespace, mergedNamespace)
        this.tentativeStore.applyTypeValuesWithDelete(namespace, mergedNamespace)
        this.currentOpenTransaction.store.applyTypeValuesWithDelete(namespace, mergedNamespace)

        return mergedNamespace
    }

    private hasRebaseValidationErrors(snapshot: SnapshotDal): boolean {
        try {
            const storeToValidate = snapshot.getStore()
            this.rebaseValidator.validateStore(storeToValidate, true, {isRebasing: true, correlationId: snapshot.id})
            return false
        } catch (e) {
            return true
        }
    }

    private rebuildOpenStore(snaps: SnapshotDal[], fragmentType?: string): void {
        const keys = this.currentOpenTransaction.store.keys()
        this.createNewOpenTransaction()
        snaps.forEach(snap => {
            const snapStore = fragmentType ? snap.getFragmentStore(fragmentType) : snap.getStore()
            this.currentOpenTransaction.store.merge(snapStore)
            keys.push(...snapStore.keys())
        })
        this.updateIndex(keys)
    }

    private removeTentativeSnapshotWithoutUpdatingTheIndex(snapshot: SnapshotDal): void {
        this.snapshots.remove(snapshot)
        this.tagManager.remove(snapshot)
    }

    private resetToApprovedState(): void {
        const pointersToUpdate = _.concat(this.tentativeStore.keys(), this.currentOpenTransaction.store.keys())
        this.tentativeStore = createStore()
        this.createNewOpenTransaction()
        this.updateIndex(pointersToUpdate)
    }

    removeConflictsAndReport(event: TransactionEvent, originalChange?: DmStore) {
        const inconsistentTentativeStore = this.tentativeStore

        this.removeConflicts()
        const changes: DmStore = this.collectChanges(inconsistentTentativeStore, originalChange)

        this.report(changes, event)
    }

    private createSnapshotTrace() {
        const snapshotChain = _(createSnapshotChain(null, this.snapshots.last(), 'createSnapshotTrace'))
            .slice(-30)
            .map('id')
            .value()
        return {
            lastApproved: this.lastApproved?.id,
            snapshotChain,
            currentTags: this.tagManager._getAllTags()
        }
    }

    private createMergeToApprovedError(changes: DmStore, id?: string) {
        return new ReportableError({
            errorType: 'cannotMergeToApprovedError',
            message: `MergeToApprovedStore called for ${id} outside initialization flow`,
            extras: {
                id,
                ...this.createSnapshotTrace(),
                content: _.keys(changes.asJson())
            }
        })
    }

    private createRemovedApprovedError(position: string, id: string | undefined) {
        return new ReportableError({
            errorType: 'removedApprovedSnapshotError',
            message: 'We just removed the approved snapshot due to a conflict',
            extras: {
                position,
                id,
                ...this.createSnapshotTrace()
            }
        })
    }

    removeConflictsReportAndVerify(change: DmStore, position: string, event: TransactionEvent, id: string) {
        this.removeConflictsAndReport(event, change)
        if (!this.snapshots.findById(id)) {
            throw this.createRemovedApprovedError(position, id)
        }
    }

    _getApprovedStoreAsJson() {
        return this.store.asJson()
    }

    _getTentativeStoreAsJson() {
        return this.tentativeStore.asJson()
    }

    _getCommittedStoreAsJson() {
        return (this.getMergedStore(false) as DmStore).asJson()
    }

    _getMergedStoreAsJson() {
        return (this.getMergedStore(true) as DmStore).asJson()
    }

    /**
     * Add the current open transaction to the snapshot chain and merge it to the tentative store.
     * Notify listeners of the commit
     * @param committer
     * @param skipValidations
     */
    commitTransactionForFragment(
        committer?: string | undefined,
        skipValidations?: boolean | undefined
    ): DmStore | null {
        const currentOpenTransactionStore = this.currentOpenTransaction.store
        if (!currentOpenTransactionStore.isEmpty()) {
            const transaction = {
                id: this.currentOpenTransaction.id,
                items: currentOpenTransactionStore.getValues()
            }
            this.callTransactionDerivedMetadata(transaction)
        }
        if (currentOpenTransactionStore.isEmpty()) {
            return null
        }
        const attempt = ++this.currentOpenTransaction.attempt
        if (!skipValidations) {
            this.validatePendingCommit({committer, attempt, openTxId: this.currentOpenTransaction.id})
        }

        const pointersModifiedInTheTransaction = currentOpenTransactionStore.keys()
        this.createNewOpenTransaction()

        if (this.isConflictingWithCommittedValue(currentOpenTransactionStore)) {
            this.updateIndex(pointersModifiedInTheTransaction)
            return null
        }

        this.tentativeStore.merge(currentOpenTransactionStore)
        return currentOpenTransactionStore
    }

    mergeToApprovedStore(changes: DmStore): void {
        this.store.merge(changes)
        this.updateIndex(changes.keys())
    }

    getAllItemsInNamespace(namespace: Namespace): Pointer[] {
        const mergedNamespaceObj = this.getMergedNamespace(namespace)
        return Object.keys(mergedNamespaceObj).map(id =>
            getFragmentPointer({
                id,
                type: namespace,
                fragmentType: this.fragmentType,
                fragmentInstance: mergedNamespaceObj[id].metaData?.fragmentInstance
            })
        )
    }

    /**
     * Validate and copies changes into the dal store. Called during DS initialization
     * @param changes - the store to copy into the dal store
     * @param snapshotId - the snapshot id to use for the changes
     */
    validateAndMergeToApprovedStore(changes: DmStore, snapshotId: string): void {
        this.validateMergeToApproved(changes, snapshotId)

        this.mergeToApprovedStore(changes)
    }

    validateMergeToApproved(changes: DmStore, snapshotId: string): void {
        if (!this.tentativeStore.isEmpty() || !this.currentOpenTransaction.store.isEmpty()) {
            if (
                !changes.intersection(this.tentativeStore).isEmpty() ||
                !changes.intersection(this.currentOpenTransaction.store).isEmpty()
            ) {
                throw this.createMergeToApprovedError(changes, snapshotId)
            }
        }
    }

    /** Rebuild the tentative store with conflicting snapshots removed.
     *
     * Conflicting snapshots are snapshots with a broken signature chain to the committed value or snapshots that
     * lead to an invalid store when applied to the approved state.
     */
    removeConflicts() {
        /* To rebase we reset the dal to the approved state since it's the last "consensus" point from the server,
         * it cannot change.
         * We then re-apply the tentative transactions, which weren't approved by the server yet, one-by-one.
         * If a tentative transaction conflicts with previous values we remove it. There is no need to send it to the
         * server since we already know it's conflicting and will be rejected.
         * We refer to that situation as a "local rejection"
         */
        const openTransaction = this.currentOpenTransaction
        const droppedSnapshots: SnapshotDal[] = []
        const pendingSnapshots = createSnapshotChain(this.lastApproved, this.snapshots.last(), 'removeConflicts')
        this.resetToApprovedState()

        for (const [i, snapshot] of pendingSnapshots.entries()) {
            /* There's no need to apply the snapshot in order to check for signature conflicts.
             * But validators work on the DAL as a whole, so we have to apply the snapshot, validate and then
             * "unapply" it if a validation conflict occurred.
             * To "unapply" we rebuild the tentative store but we only use the snapshots that weren't
             * already locally rejected.
             */
            const snapshotStore = snapshot.getFragmentStore(this.fragmentType)
            if (this.isConflictingWithMergedValue(snapshotStore)) {
                droppedSnapshots.push(snapshot)
                this.removeTentativeSnapshotWithoutUpdatingTheIndex(snapshot)
            } else {
                this.currentOpenTransaction.store.merge(snapshotStore)
                this.updateIndex(snapshotStore.keys())
                if (this.hasRebaseValidationErrors(snapshot)) {
                    this.debugLogger.info(
                        `dmDal rebase is removing a snapshot due to validation conflict. Snapshot id: ${snapshot.id}`
                    )
                    droppedSnapshots.push(snapshot)
                    this.removeTentativeSnapshotWithoutUpdatingTheIndex(snapshot)
                    const validPendingSnapshotsUpToThisPoint = _(pendingSnapshots)
                        .take(i)
                        .without(...droppedSnapshots)
                        .value()
                    this.rebuildOpenStore(validPendingSnapshotsUpToThisPoint, this.fragmentType)
                }
            }
        }
        this.tentativeStore.merge(this.currentOpenTransaction.store)

        this.currentOpenTransaction = openTransaction
        this.updateIndex(openTransaction.store.keys())
        if (droppedSnapshots.length) {
            this.eventEmitter.emit(SNAPSHOT_REMOVED_EVENT, droppedSnapshots)
        }
    }

    getNamespaces(): Namespace[] {
        return Array.from(this.getMergedStore(true)._store.keys())
    }
}

export const createFragmentDal = ({
    fragmentType,
    coreConfig,
    queryFilterGetters,
    postSetOperations,
    dmTypes,
    customGetters,
    eventEmitter,
    lastApproved,
    snapshots,
    tagManager,
    customGettersEnabled
}: CreateFragmentArgs): FragmentDal => {
    return new FragmentDal(
        fragmentType,
        coreConfig,
        queryFilterGetters,
        postSetOperations,
        dmTypes,
        customGetters,
        eventEmitter,
        lastApproved,
        snapshots,
        tagManager,
        customGettersEnabled
    )
}
